import React, { useMemo, useState } from "react";
import LibraryDataTable from "react-data-table-component";

export * from "react-data-table-component";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const removeNestedStyleRules = (style: Record<string, any> = {}) =>
  Object.entries(style).reduce((acc, [key, value]) => {
    if (!key.startsWith("&")) {
      acc[key] = value;
    }

    return acc;
  }, {} as Record<string, any>);

const getValueByPath = (row: any, path: string) =>
  path.split(".").reduce((acc, key) => acc?.[key], row);

const getCellContent = (column: any, row: any, rowIndex: number) => {
  if (typeof column.cell === "function") {
    return column.cell(row, rowIndex);
  }

  if (typeof column.format === "function") {
    return column.format(row, rowIndex);
  }

  if (typeof column.selector === "function") {
    return column.selector(row, rowIndex);
  }

  if (typeof column.selector === "string") {
    return getValueByPath(row, column.selector);
  }

  return null;
};

const getCellAlignmentClassName = (column: any) => {
  if (column.right) {
    return "app-data-table__cell--align-right";
  }

  if (column.center) {
    return "app-data-table__cell--align-center";
  }

  return "";
};

const getConditionalRowStyle = (conditionalRowStyles: any[] = [], row: any) =>
  conditionalRowStyles.reduce((acc, rule) => {
    if (typeof rule?.when === "function" && rule.when(row)) {
      return {
        ...acc,
        ...removeNestedStyleRules(rule.style),
      };
    }

    return acc;
  }, {});

const isComplexTable = (props: any) =>
  Boolean(
    props.selectableRows ||
      props.fixedHeader ||
      props.persistTableHead ||
      props.subHeader ||
      props.subHeaderComponent ||
      props.sortServer ||
      props.onSort ||
      props.paginationComponent
  );

const NativePagination = ({
  currentPage,
  dataLength,
  onChangePage,
  onChangeRowsPerPage,
  paginationComponentOptions,
  paginationRowsPerPageOptions,
  perPage,
  totalRows,
}: any) => {
  const rowsPerPageText =
    paginationComponentOptions?.rowsPerPageText ?? "Rows per page:";
  const rangeSeparatorText =
    paginationComponentOptions?.rangeSeparatorText ?? "of";
  const rowsPerPageOptions =
    paginationRowsPerPageOptions?.length > 0
      ? paginationRowsPerPageOptions
      : DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const totalPages = Math.max(1, Math.ceil((totalRows || 0) / perPage));
  const from = totalRows === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to =
    totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + dataLength, totalRows);

  return (
    <div className="app-data-table__footer">
      <div>
        {from}-{to} {rangeSeparatorText} {totalRows}
      </div>
      <div className="app-data-table__footer-controls">
        <label>
          {rowsPerPageText}
          <select
            className="app-data-table__rows-select"
            value={perPage}
            onChange={(event) =>
              onChangeRowsPerPage(Number(event.target.value), 1)
            }
          >
            {rowsPerPageOptions.map((option: number) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <button
          className="app-data-table__page-button"
          disabled={currentPage <= 1}
          onClick={() => onChangePage(currentPage - 1)}
        >
          Previous
        </button>
        <span>
          Page {currentPage} / {totalPages}
        </span>
        <button
          className="app-data-table__page-button"
          disabled={currentPage >= totalPages}
          onClick={() => onChangePage(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const NativeTable = ({
  columns = [],
  conditionalRowStyles = [],
  data = [],
  expandableIcon,
  expandableInheritConditionalStyles,
  expandableRowDisabled,
  expandableRows,
  expandableRowsComponent,
  highlightOnHover,
  keyField = "id",
  noDataComponent = null,
  onChangePage,
  onChangeRowsPerPage,
  onRowExpandToggled,
  onRowClicked,
  pagination,
  paginationComponentOptions,
  paginationDefaultPage = 1,
  paginationPerPage = 10,
  paginationRowsPerPageOptions,
  paginationServer,
  paginationTotalRows,
  pointerOnHover,
  progressComponent,
  progressPending,
  striped,
}: any) => {
  const [currentPage, setCurrentPage] = useState(paginationDefaultPage);
  const [rowsPerPage, setRowsPerPage] = useState(paginationPerPage);
  const [expandedKeys, setExpandedKeys] = useState<Array<string | number>>([]);

  const visibleColumns = useMemo(
    () => columns.filter((column: any) => !column.omit),
    [columns]
  );

  const totalRows = paginationServer
    ? paginationTotalRows ?? data.length
    : data.length;

  const renderedRows = useMemo(() => {
    if (!pagination || paginationServer) {
      return data;
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, data, pagination, paginationServer, rowsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onChangePage?.(page);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number, nextPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setCurrentPage(nextPage);
    onChangeRowsPerPage?.(nextRowsPerPage, nextPage);
  };

  const toggleExpandedRow = (row: any) => {
    const rowKey = row?.[keyField];
    const nextExpanded = !expandedKeys.includes(rowKey);

    setExpandedKeys((prev) =>
      nextExpanded ? [...prev, rowKey] : prev.filter((key) => key !== rowKey)
    );
    onRowExpandToggled?.(nextExpanded, row);
  };

  if (progressPending) {
    return <>{progressComponent ?? null}</>;
  }

  if (!renderedRows?.length) {
    return <>{noDataComponent}</>;
  }

  return (
    <div className="app-data-table-wrapper">
      <table className="app-data-table">
        <thead>
          <tr>
            {expandableRows ? (
              <th className="app-data-table__expand-column"></th>
            ) : null}
            {visibleColumns.map((column: any, columnIndex: number) => (
              <th
                key={column.id ?? column.name ?? column.selector ?? columnIndex}
                className={getCellAlignmentClassName(column)}
              >
                {column.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderedRows.map((row: any, rowIndex: number) => {
            const rowStyle = getConditionalRowStyle(conditionalRowStyles, row);
            const rowKey = row?.[keyField] ?? rowIndex;
            const isExpanded = expandedKeys.includes(rowKey);
            const rowExpandDisabled = expandableRowDisabled?.(row);
            const rowClassNames = [
              striped && rowIndex % 2 === 1 ? "app-data-table__row--striped" : "",
              highlightOnHover ? "app-data-table__row--hover" : "",
              pointerOnHover || onRowClicked ? "app-data-table__row--clickable" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <React.Fragment key={rowKey}>
                <tr
                  className={rowClassNames}
                  style={rowStyle}
                  onClick={() => onRowClicked?.(row)}
                >
                  {expandableRows ? (
                    <td className="app-data-table__expand-column">
                      {!rowExpandDisabled ? (
                        <button
                          type="button"
                          className="app-data-table__expand-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpandedRow(row);
                          }}
                        >
                          {isExpanded
                            ? expandableIcon?.expanded ?? "-"
                            : expandableIcon?.collapsed ?? "+"}
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                  {visibleColumns.map((column: any, columnIndex: number) => (
                    <td
                      key={
                        column.id ?? column.name ?? column.selector ?? `${rowIndex}-${columnIndex}`
                      }
                      className={getCellAlignmentClassName(column)}
                      style={removeNestedStyleRules(column.style)}
                    >
                      {getCellContent(column, row, rowIndex)}
                    </td>
                  ))}
                </tr>
                {expandableRows && isExpanded ? (
                  <tr
                    className={
                      expandableInheritConditionalStyles ? rowClassNames : ""
                    }
                    style={expandableInheritConditionalStyles ? rowStyle : undefined}
                  >
                    <td
                      colSpan={visibleColumns.length + 1}
                      className="app-data-table__expanded-content"
                    >
                      {expandableRowsComponent?.({ data: row })}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {pagination ? (
        <NativePagination
          currentPage={currentPage}
          dataLength={renderedRows.length}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          paginationComponentOptions={paginationComponentOptions}
          paginationRowsPerPageOptions={paginationRowsPerPageOptions}
          perPage={rowsPerPage}
          totalRows={totalRows}
        />
      ) : null}
    </div>
  );
};

const AppDataTable = ({ columns, customStyles, responsive, ...props }: any) => {
  if (isComplexTable(props)) {
    return (
      <LibraryDataTable
        {...props}
        responsive={responsive ?? true}
        columns={columns}
        customStyles={customStyles}
      />
    );
  }

  return <NativeTable {...props} columns={columns} />;
};

export default AppDataTable;
